import { Control, Controller, FieldValues, Path } from "react-hook-form";
import FieldGridContainer from "../fieldGridContainer";
import { Autocomplete, TextField } from "@mui/material";

export type RenderAutocompleteProps = {
  /** Option shape is `id` / `label` in `renderOption`; callers use varied sources. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- option rows come from multiple modules
  dataOptions: any[];
  isMultiSelect?: boolean;
  disabled?: boolean;
};

export type ControlledAutocompleteProps<T extends FieldValues = FieldValues> =
  RenderAutocompleteProps & {
    control?: Control<T>;
    isGridItem?: boolean;
    label: string;
    id: string;
    name: Path<T>;
    helperText?: string;
    errorMessage?: string;
    required?: boolean;
    dataTestId?: string;
  };

/** RHF Control<T> is invariant; bridge internally for callers. */
function ControlledAutocomplete<T extends FieldValues = FieldValues>(
  props: ControlledAutocompleteProps<T>,
) {
  const {
    control,
    isGridItem = true,
    label,
    id,
    name,
    helperText = "",
    errorMessage,
    dataTestId,
    ...autocompleteProps
  } = props;

  const resolvedControl = control as unknown as Control<FieldValues>;
  const resolvedName = name as string;

  if (isGridItem) {
    return (
      <FieldGridContainer>
        <Controller
          name={resolvedName}
          control={resolvedControl}
          render={({ field }) => (
            <Autocomplete
              disablePortal
              id={id}
              options={autocompleteProps?.dataOptions ?? []}
              sx={{ width: "100%" }}
              multiple={autocompleteProps?.isMultiSelect}
              disabled={autocompleteProps?.disabled}
              value={field.value ?? (autocompleteProps?.isMultiSelect ? [] : null)}
              onChange={(_, data) => field.onChange(data)}
              isOptionEqualToValue={
                autocompleteProps?.isMultiSelect
                  ? undefined
                  : (option, value) => option?.id === value?.id
              }
              getOptionLabel={(option) =>
                option?.label != null ? String(option.label) : ""
              }
              data-testid={dataTestId || id}
              renderInput={(fieldParams) => (
                <TextField
                  {...fieldParams}
                  label={label}
                  helperText={errorMessage ?? helperText}
                  error={Boolean(errorMessage)}
                  variant="outlined"
                  size="small"
                  required={props.required}
                />
              )}
              renderOption={(itemProps, option) => {
                return (
                  <li {...itemProps} key={option.id}>
                    {option.label}
                  </li>
                );
              }}
            />
          )}
        />
      </FieldGridContainer>
    );
  }

  return (
    <Controller
      name={resolvedName}
      control={resolvedControl}
      render={({ field }) => (
        <Autocomplete
          disablePortal
          id={id}
          options={autocompleteProps?.dataOptions ?? []}
          sx={{ width: "100%" }}
          multiple={autocompleteProps?.isMultiSelect}
          disabled={autocompleteProps?.disabled}
          value={field.value ?? (autocompleteProps?.isMultiSelect ? [] : null)}
          onChange={(_, data) => field.onChange(data)}
          isOptionEqualToValue={
            autocompleteProps?.isMultiSelect
              ? undefined
              : (option, value) => option?.id === value?.id
          }
          getOptionLabel={(option) =>
            option?.label != null ? String(option.label) : ""
          }
          data-testid={dataTestId || id}
          renderInput={(fieldParams) => (
            <TextField
              {...fieldParams}
              label={label}
              variant="outlined"
              size="small"
              helperText={errorMessage ?? helperText}
              error={Boolean(errorMessage)}
              required={props.required}
            />
          )}
          renderOption={(itemProps, option) => {
            return (
              <li {...itemProps} key={option.id}>
                {option.label}
              </li>
            );
          }}
        />
      )}
    />
  );
}

export default ControlledAutocomplete;
